import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCallback, useMemo, useState } from 'react'
import { Alpha3Code } from 'i18n-iso-countries'

import { VerificationsProps } from './VerificationsTable.types'
import { dayJS, getFormattedDate } from '@percent/partner-dashboard/common/library/utility/date'
import { ErrorView, TableCellStatusIcon } from '@percent/partner-dashboard/common/components'
import { ReactComponent as RightArrowIcon } from '@percent/partner-dashboard/common/assets/images/right-arrow-icon.svg'
import styles from './VerificationsTable.module.scss'
import {
  Button,
  Chip,
  DateRange,
  FilterList,
  Flex,
  FlagAvatar,
  Icon,
  SearchFilterList,
  SearchInput,
  Tooltip,
  Table,
  TableBody,
  TableProvider,
  TablePagination,
  TableRow,
  TableCell
} from '@percent/lemonade'
import { useAgentEmailSearch, useProgramNameFilter, useStatusesFilter } from './hooks'
import { useOrganisationNameFilter } from './hooks/useOrganisationNameFilter'
import { ValidationSubmission } from '@percent/partner-dashboard/services/applications/applicationsService.types'
import { useCountryFilter } from './hooks/useCountryFilter'
import { useCountries } from '@percent/partner-dashboard/common/hooks/useCountries/useCountries'
import { useMapRedactedToLabel } from './hooks/useMapRedactedToLabel'

function CountryCell({
  countryCode,
  getCountryNameByCode,
  className
}: Readonly<{
  countryCode: Alpha3Code | undefined
  getCountryNameByCode: (code: Alpha3Code) => string
  className: string
}>) {
  if (!countryCode) return null

  return (
    <TableCell>
      <div className={className}>
        <FlagAvatar code3={countryCode} />
        <span>{getCountryNameByCode(countryCode)}</span>
      </div>
    </TableCell>
  )
}

export function VerificationsTable({
  data,
  isLoading,
  totalResults,
  errorMessage,
  nextPage,
  previousPage,
  queryParams,
  setQueryParams
}: Readonly<VerificationsProps>) {
  const { t } = useTranslation()
  const { getCountryNameByCode } = useCountries()
  const { mapRedactedToLabel } = useMapRedactedToLabel()
  const [isDatePickerOpened, setIsDatePickerOpened] = useState(false)
  const { email, onEmailChange, handleClearEmailValue, handleKeyPress, validateEmail, emailError } =
    useAgentEmailSearch({
      queryParams,
      setQueryParams
    })
  const { statusesOptions, selectedStatuses, onStatusChange } = useStatusesFilter({ queryParams, setQueryParams })
  const { programNameOptions, isProgramNameFilterLoading, selectedProgramNames, onProgramNameChange } =
    useProgramNameFilter({
      queryParams,
      setQueryParams
    })
  const {
    organisationNameOptions,
    isOrganisationNameFilterLoading,
    selectedOrganisations,
    onOrganisationNameChange,
    searchValue,
    onSearchChange,
    onClearSearchValue,
    handleKeyPress: onOrganisationKeyPress,
    onOrganisationNameSearchButtonClick
  } = useOrganisationNameFilter({
    queryParams,
    setQueryParams
  })
  const {
    countryOptions,
    isCountryFilterLoading,
    selectedCountries,
    onCountryChange,
    countrySearchValue,
    onCountrySearchChange,
    onClearCountrySearchValue,
    handleKeyPress: onCountryKeyPress,
    onCountrySearchButtonClick
  } = useCountryFilter({
    queryParams,
    setQueryParams
  })

  const columns = [
    { id: t('table.header.agentEmail') },
    { id: t('table.header.organisation') },
    { id: t('table.header.country') },
    ...(programNameOptions.length > 1 ? [{ id: t('table.header.programName') }] : []),
    { id: t('table.header.overallResult'), props: { width: '110px' } },
    {
      id: t('table.header.applicationDate'),
      props: { width: '170px' }
    }
  ]

  const navigate = useNavigate()

  const rowVerificationClicked = (applicationId: string, e: React.MouseEvent) => {
    const url = `/verifications/${applicationId}`

    if (e.ctrlKey || e.metaKey) {
      window.open(url, '_blank')
    } else {
      navigate(url)
    }
  }
  const agentEmail = (aEmail: string | null) => aEmail ?? ''

  const removeOrganisationNameOptionFilter = () => {
    setQueryParams({
      ...queryParams,
      organisationIds: [],
      validationRequestIds: []
    })
  }

  const removeCountryOptionFilter = () => {
    setQueryParams({
      ...queryParams,
      countryCodes: []
    })
  }

  const handleDateRangeClick = useCallback(() => {
    setIsDatePickerOpened(prev => !prev)
  }, [])

  const handleClearDate = useCallback(() => {
    setQueryParams({ ...queryParams, startDate: undefined, endDate: undefined })
  }, [setQueryParams, queryParams])

  const dateRange = useMemo(
    () => queryParams.startDate && queryParams.endDate,
    [queryParams.startDate, queryParams.endDate]
  )

  const selectedOrganisationFilters = useMemo(
    () =>
      organisationNameOptions.filter(
        option =>
          queryParams?.organisationIds?.includes(option.value.organisationId) ||
          queryParams?.validationRequestIds?.includes(option.value.validationRequestId)
      ),
    [queryParams, organisationNameOptions]
  )

  const selectedCountryFilters = useMemo(
    () => countryOptions.filter(option => queryParams?.countryCodes?.includes(option.value)),
    [queryParams, countryOptions]
  )

  const filters = useMemo(
    () => (
      <div>
        <div className={styles.filtersContainer}>
          <SearchInput
            name="email"
            value={email}
            handleClearValue={handleClearEmailValue}
            onKeyDown={handleKeyPress}
            onChange={onEmailChange}
            onBlur={validateEmail}
            placeholder={t('table.verificationSearchPlaceholder')}
            status={emailError ? 'danger' : 'default'}
            statusMessage={emailError}
            width="480px"
          />

          <div className={styles.filtersWrapper}>
            <SearchFilterList
              options={organisationNameOptions}
              isLoading={isOrganisationNameFilterLoading}
              selectedValues={selectedOrganisations}
              onChange={onOrganisationNameChange}
              searchValue={searchValue}
              onSearchChange={onSearchChange}
              handleClearValue={onClearSearchValue}
              onKeyDown={onOrganisationKeyPress}
              onButtonClick={onOrganisationNameSearchButtonClick}
              noResultsFoundText={
                searchValue.length < 3
                  ? t('typography.organisationSearchNameFilter.noOptionsLessThan3Chars')
                  : undefined
              }
              keyName="id"
              testId="organisation-name-search-filter"
              buttonText={t('typography.organisationSearchNameFilter.button')}
              placeholder={t('typography.organisationSearchNameFilter.placeholder')}
              label={t('table.header.organisation')}
              placement="bottom"
            />
            <SearchFilterList
              options={countryOptions}
              isLoading={isCountryFilterLoading}
              selectedValues={selectedCountries}
              onChange={onCountryChange}
              searchValue={countrySearchValue}
              onSearchChange={onCountrySearchChange}
              handleClearValue={onClearCountrySearchValue}
              onKeyDown={onCountryKeyPress}
              onButtonClick={onCountrySearchButtonClick}
              testId="country-search-filter"
              buttonText={t('typography.organisationSearchNameFilter.button')}
              placeholder={t('typography.countrySearchFilter.placeholder')}
              label={t('table.header.country')}
              placement="bottom"
            />
            {programNameOptions.length > 1 && (
              <FilterList
                label={t('table.header.programName')}
                options={programNameOptions}
                selectedValues={selectedProgramNames}
                onChange={onProgramNameChange}
                isLoading={isProgramNameFilterLoading}
                testId="program-filter"
              />
            )}
            <FilterList
              label={t('table.header.overallResult')}
              options={statusesOptions}
              selectedValues={selectedStatuses}
              onChange={onStatusChange}
              testId="overall-result-filter"
            />
            <Flex align="center" gap={8}>
              <DateRange
                open={isDatePickerOpened}
                setOpen={setIsDatePickerOpened}
                setQueryParams={params => {
                  if (typeof params === 'object') {
                    setQueryParams({
                      ...queryParams,
                      startDate: params?.startDate,
                      endDate: params?.endDate
                    })
                  }
                }}
                queryParams={{
                  startDate: queryParams.startDate,
                  endDate: queryParams.endDate
                }}
                maxDate={new Date()}
                data-testid="verifications-date-range"
              >
                <button onClick={handleDateRangeClick} className={styles.dateButton} type="button">
                  <Flex align="center" gap={4}>
                    <span>
                      {(dateRange &&
                        `${dayJS(queryParams.startDate).format('ll')} - ${dayJS(queryParams.endDate).format('ll')}`) ||
                        t('button.dateAll')}
                    </span>
                    <Icon name={isDatePickerOpened ? 'chevron-up' : 'chevron-down'} size={3} color="black" />
                  </Flex>
                </button>
              </DateRange>
              {dateRange && (
                <Button variant="primary" size="small" onClick={handleClearDate}>
                  {t('typography.clearDate')}
                </Button>
              )}
            </Flex>
          </div>
        </div>
        {!!selectedOrganisationFilters.length && (
          <div className={styles.filtersChipContainer}>
            <Chip
              label={t('table.header.organisation')}
              value={selectedOrganisationFilters.map(option => option.label).join(', ')}
              onClick={() => removeOrganisationNameOptionFilter()}
              testId="organisation-filter-chip"
            />
          </div>
        )}
        {!!selectedCountryFilters.length && (
          <div className={styles.filtersChipContainer}>
            <Chip
              label={t('table.header.country')}
              value={selectedCountryFilters.map(option => option.label).join(', ')}
              onClick={() => removeCountryOptionFilter()}
              testId="country-filter-chip"
            />
          </div>
        )}
        {dateRange && (
          <div className={styles.filtersChipContainer}>
            <Chip
              label={t('table.header.applicationDate')}
              value={`${dayJS(queryParams.startDate).format('ll')} - ${dayJS(queryParams.endDate).format('ll')}`}
              onClick={handleClearDate}
              testId="date-range-filter-chip"
            />
          </div>
        )}
      </div>
    ),
    [
      email,
      handleKeyPress,
      t,
      programNameOptions,
      selectedProgramNames,
      onProgramNameChange,
      isProgramNameFilterLoading,
      statusesOptions,
      selectedStatuses,
      onStatusChange,
      isDatePickerOpened,
      dateRange,
      handleDateRangeClick,
      handleClearDate,
      queryParams
    ]
  )

  const getRowCountryCode = (row: ValidationSubmission): Alpha3Code | undefined =>
    row?.organisation?.countryCode || row?.validationRequest?.countryCode

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <TableProvider
      disableHorizontalScroll
      columns={columns}
      totalResults={totalResults}
      isLoading={isLoading || !data}
      illustration="no-results"
      emptyTableTitle={t(email ? 'typography.noSearchResults' : 'typography.noRequestsTable')}
      tableHeadTestId="validation-submissions-table-header"
    >
      {filters}
      <Table>
        <TableBody>
          {data?.map(row => (
            <TableRow
              key={row?.id}
              onClick={e => rowVerificationClicked(row.id, e)}
              data-testid={`table-row-${row.id}`}
            >
              <Tooltip content={agentEmail(row.agentVerificationEmail)}>
                <TableCell className={agentEmail(row.agentVerificationEmail) ? styles.tooltipCell : styles.naCell}>
                  {mapRedactedToLabel(agentEmail(row.agentVerificationEmail)) || t('status.notAvailable')}
                </TableCell>
              </Tooltip>
              <TableCell>{row.organisationName ?? row.validationRequestName}</TableCell>
              <CountryCell
                countryCode={getRowCountryCode(row)}
                getCountryNameByCode={getCountryNameByCode}
                className={styles.countryDetailsWrapper}
              />
              {programNameOptions.length > 1 && (
                <TableCell>{row.configuration?.name || t('typography.unnamedProgram')}</TableCell>
              )}
              <TableCell>
                <TableCellStatusIcon statusType={row?.status} />
              </TableCell>
              <TableCell>{getFormattedDate(row?.createdAt)}</TableCell>
              <TableCell className={styles.arrow}>
                <RightArrowIcon />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!isLoading && (
        <TablePagination
          totalResultsText={t('table.results', { count: totalResults || undefined })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      )}
    </TableProvider>
  )
}
